/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CancellationDetails } from '@localstack/types';
import type { CIAuthToken } from '@localstack/types';
import type { ComputeUsageSummary } from '@localstack/types';
import type { CreateCiTokenRequest } from '@localstack/types';
import type { CreateSubscriptionRequest } from '@localstack/types';
import type { Discount } from '@localstack/types';
import type { EmulatorType } from '@localstack/types';
import type { Invoice } from '@localstack/types';
import type { LicenseAssignment } from '@localstack/types';
import type { Plan } from '@localstack/types';
import type { Subscription } from '@localstack/types';
import type { SubscriptionCIAuthToken } from '@localstack/types';
import type { TaxDetails } from '@localstack/types';
import type { UpdateSubscriptionRequest } from '@localstack/types';
import type { UsageSummary } from '@localstack/types';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class SubscriptionService {
  /**
   * @param emulator Emulator product to return plans for (e.g. "aws" or "snowflake"). When omitted, the emulator is inferred from the organization's existing subscriptions.
   *
   * @returns Plan
   * @throws ApiError
   */
  public static listPlans(emulator?: EmulatorType): CancelablePromise<Array<Plan>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/plans',
      query: {
        emulator: emulator,
      },
    });
  }

  /**
   * @param planId
   * @param promotionCode
   * @returns any
   * @throws ApiError
   */
  public static getPromotion(planId: string, promotionCode: string): CancelablePromise<Discount> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/plans/{plan_id}/promotions/{promotion_code}',
      path: {
        plan_id: planId,
        promotion_code: promotionCode,
      },
    });
  }

  /**
   * @param requestBody
   * @returns Subscription
   * @throws ApiError
   */
  public static createSubscription(requestBody?: CreateSubscriptionRequest): CancelablePromise<Subscription> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v1/plans/subscriptions',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @returns Subscription
   * @throws ApiError
   */
  public static listSubscriptions(): CancelablePromise<Array<Subscription>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/plans/subscriptions',
    });
  }

  /**
   * @param subscriptionId
   * @param requestBody
   * @returns Subscription
   * @throws ApiError
   */
  public static updateSubscription(
    subscriptionId: string,
    requestBody?: UpdateSubscriptionRequest,
  ): CancelablePromise<Subscription> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/v1/plans/subscriptions/{subscription_id}',
      path: {
        subscription_id: subscriptionId,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param subscriptionId
   * @returns Subscription
   * @throws ApiError
   */
  public static getSubscription(subscriptionId: string): CancelablePromise<Subscription> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/plans/subscriptions/{subscription_id}',
      path: {
        subscription_id: subscriptionId,
      },
    });
  }

  /**
   * @param subscriptionId
   * @param requestBody
   * @returns Subscription
   * @throws ApiError
   */
  public static cancelSubscription(
    subscriptionId: any,
    requestBody?: CancellationDetails,
  ): CancelablePromise<Subscription> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/v1/plans/subscriptions/{subscription_id}',
      path: {
        subscription_id: subscriptionId,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @returns any
   * @throws ApiError
   */
  public static getAggregatedSubscriptionInfo(): CancelablePromise<{
    subscriptions?: Array<Subscription>;
    usageData?: Record<string, UsageSummary>;
    ci_tokens?: Array<CIAuthToken>;
    licenseAssignments?: Array<LicenseAssignment>;
    computeUsage?: ComputeUsageSummary;
  }> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/plans/subscriptions/aggregated',
    });
  }

  /**
   * @param subscriptionId
   * @returns UsageSummary
   * @throws ApiError
   */
  public static getUsage(subscriptionId: any): CancelablePromise<UsageSummary> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/plans/subscriptions/{subscription_id}/usage',
      path: {
        subscription_id: subscriptionId,
      },
    });
  }

  /**
   * @param requestBody
   * @returns CIAuthToken
   * @throws ApiError
   */
  public static createCiToken(requestBody?: CreateCiTokenRequest): CancelablePromise<CIAuthToken> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v1/plans/ci-tokens',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @returns SubscriptionCIAuthToken
   * @throws ApiError
   */
  public static listCiTokens(): CancelablePromise<Array<SubscriptionCIAuthToken>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/plans/ci-tokens',
    });
  }

  /**
   * @param tokenId
   * @param requestBody
   * @returns CIAuthToken
   * @throws ApiError
   */
  public static updateCiToken(
    tokenId: any,
    requestBody?: {
      name?: string;
    },
  ): CancelablePromise<CIAuthToken> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/v1/plans/ci-tokens/{token_id}',
      path: {
        token_id: tokenId,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param tokenId
   * @returns CIAuthToken
   * @throws ApiError
   */
  public static deleteCiToken(tokenId: string): CancelablePromise<CIAuthToken> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/v1/plans/ci-tokens/{token_id}',
      path: {
        token_id: tokenId,
      },
    });
  }

  /**
   * @param tokenId
   * @returns CIAuthToken
   * @throws ApiError
   */
  public static rotateCiToken(tokenId: any): CancelablePromise<CIAuthToken> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v1/plans/ci-tokens/{token_id}/rotate',
      path: {
        token_id: tokenId,
      },
    });
  }

  /**
   * @returns Invoice
   * @throws ApiError
   */
  public static listInvoices(): CancelablePromise<Array<Invoice>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/plans/invoices',
    });
  }

  /**
   * @param planId
   * @param seats
   * @param promoCode
   * @returns TaxDetails
   * @throws ApiError
   */
  public static calculateTax(planId: string, seats: number, promoCode?: string): CancelablePromise<TaxDetails> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/plans/{plan_id}/tax',
      path: {
        plan_id: planId,
      },
      query: {
        seats: seats,
        promo_code: promoCode,
      },
    });
  }
}
